import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, InputNumber, Radio, Select, Space, Table, Tag, Typography, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import {
  downloadBulkTemplate,
  listBulkTemplates,
  uploadBulkBatch,
  type BulkBatch,
  type BulkSubjectType,
  type BulkTemplate,
} from '@/api/bulk';
import { normalizeApiError, type NormalizedApiError } from '@/api/errors';
import { listFlows } from '@/api/flows';
import { listScreenConfigs } from '@/api/screens';
import { ulid } from '@/api/ulid';
import { columnRequired, defaultBatchName, repeatReasonProblem, SUBJECT_TYPE_HELP, SUBJECT_TYPE_LABEL } from './bulkModel';

const { Text, Paragraph } = Typography;

const MAX_BYTES = 5 * 1024 * 1024;

export interface UploadDrawerProps {
  open: boolean;
  /** Pre-selects a flow, when arriving from the Flows page. */
  flowId?: string;
  onClose: () => void;
  /** The file was checked. Nothing has started: the batch page takes it from here. */
  onValidated: (batch: BulkBatch) => void;
}

export function UploadDrawer({ open, flowId, onClose, onValidated }: UploadDrawerProps) {
  const [flows, setFlows] = useState<{ value: string; label: string }[]>([]);
  const [screens, setScreens] = useState<{ value: string; label: string }[]>([]);
  const [templates, setTemplates] = useState<BulkTemplate[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<string | undefined>(flowId);
  const [type, setType] = useState<BulkSubjectType>('INDIVIDUAL');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [screen, setScreen] = useState<string | undefined>();
  const [maxPerGroup, setMaxPerGroup] = useState<number | null>(9);
  const [repeat, setRepeat] = useState(false);
  const [reason, setReason] = useState('');
  const [idemKey, setIdemKey] = useState(() => ulid());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<NormalizedApiError | null>(null);
  const [reused, setReused] = useState<BulkBatch | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedFlow(flowId);
    setFile(null);
    setName('');
    setError(null);
    setReused(null);
    setRepeat(false);
    setReason('');
    setIdemKey(ulid());
    let live = true;
    void listFlows()
      .then((all) => {
        if (!live) return;
        setFlows(
          all
            .filter((f) => f.publishedVersion != null)
            .map((f) => ({ value: f.id, label: f.name && f.name !== f.id ? `${f.name} · ${f.id} (v${f.publishedVersion})` : `${f.id} (v${f.publishedVersion})` })),
        );
      })
      .catch((e) => live && setError(normalizeApiError(e)));
    void listBulkTemplates()
      .then((t) => live && setTemplates(t))
      .catch((e) => live && setError(normalizeApiError(e)));
    // Room links are optional, and someone without screens.read can still upload.
    void listScreenConfigs()
      .then((all) => {
        if (!live) return;
        const published = new Set(all.filter((s) => s.status === 'PUBLISHED').map((s) => s.configId));
        setScreens([...published].map((id) => ({ value: id, label: id })));
      })
      .catch(() => live && setScreens([]));
    return () => {
      live = false;
    };
  }, [open, flowId]);

  const columns = useMemo(() => templates.find((t) => t.subjectType === type)?.columns ?? [], [templates, type]);
  const inviting = Boolean(screen);
  const reasonProblem = repeat ? repeatReasonProblem(reason) : null;
  const ready = Boolean(selectedFlow && file && name.trim()) && !reasonProblem;

  const submit = useCallback(async () => {
    if (!selectedFlow || !file) return;
    setBusy(true);
    setError(null);
    setReused(null);
    try {
      const batch = await uploadBulkBatch(
        {
          flowId: selectedFlow,
          subjectType: type,
          name: name.trim(),
          screenConfigId: screen,
          maxPerGroup: type === 'CONNECTION_GROUP' && maxPerGroup ? maxPerGroup : undefined,
          repeatReason: repeat ? reason.trim() : undefined,
        },
        file,
        idemKey,
      );
      if (batch.reused) {
        setReused(batch);
      } else {
        onValidated(batch);
      }
    } catch (e) {
      setError(normalizeApiError(e));
      setIdemKey(ulid()); // a refused file is fixed and sent again as a new request
    } finally {
      setBusy(false);
    }
  }, [selectedFlow, file, type, name, screen, maxPerGroup, repeat, reason, idemKey, onValidated]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={620}
      title="Upload a CSV"
      extra={
        <Button type="primary" disabled={!ready} loading={busy} onClick={() => void submit()} data-testid="bulk-validate">
          Check the file
        </Button>
      }
    >
      <Paragraph type="secondary">Every row is checked first. Nothing starts until you confirm.</Paragraph>
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error.message}
          description={<Text className="ol-mono">{error.code}</Text>}
          data-testid="bulk-upload-error"
        />
      )}
      {reused && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="bulk-reused"
          message="This file was already uploaded for this flow."
          description={
            <Space direction="vertical">
              <Text>Nothing new was created. To run these subjects again on purpose, say why below.</Text>
              <Space>
                <Button size="small" onClick={() => onValidated(reused)}>
                  Open the earlier batch
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setRepeat(true);
                    setReused(null);
                    setIdemKey(ulid());
                  }}
                >
                  Run them again
                </Button>
              </Space>
            </Space>
          }
        />
      )}
      <Form layout="vertical">
        <Form.Item label="Flow" required>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="A published flow"
            options={flows}
            value={selectedFlow}
            onChange={setSelectedFlow}
            data-testid="bulk-flow"
            notFoundContent="No published flows"
          />
        </Form.Item>
        <Form.Item label="What each row is" extra={SUBJECT_TYPE_HELP[type]}>
          <Radio.Group value={type} onChange={(e) => setType(e.target.value as BulkSubjectType)} data-testid="bulk-type">
            {(Object.keys(SUBJECT_TYPE_LABEL) as BulkSubjectType[]).map((t) => (
              <Radio.Button key={t} value={t}>
                {SUBJECT_TYPE_LABEL[t]}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item label="Template">
          <Button icon={<DownloadOutlined />} onClick={() => void downloadBulkTemplate(type)} data-testid="bulk-template">
            Download the {SUBJECT_TYPE_LABEL[type].toLowerCase()} template
          </Button>
        </Form.Item>
        <Form.Item label="File" required>
          <Upload.Dragger
            accept=".csv,text/csv"
            maxCount={1}
            showUploadList={Boolean(file)}
            fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}
            onRemove={() => setFile(null)}
            beforeUpload={(f) => {
              if (f.size > MAX_BYTES) {
                setError({ code: 'PAYLOAD_TOO_LARGE', message: 'The file is larger than 5 MB.', httpStatus: 413 });
                return Upload.LIST_IGNORE;
              }
              setError(null);
              setFile(f);
              setName((current) => current || defaultBatchName(f.name));
              setIdemKey(ulid());
              return false; // sent on "Check the file", not now
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drop a CSV here</p>
            <p className="ant-upload-hint">UTF-8, up to 5 MB and 10,000 rows. Never include Aadhaar numbers.</p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item label="Batch name" required>
          <Input value={name} maxLength={200} onChange={(e) => setName(e.target.value)} data-testid="bulk-name" />
        </Form.Item>
        <Form.Item
          label="Send each subject a room link"
          extra="Optional. Goes out after the run starts, through the hub's communication provider."
        >
          <Select allowClear placeholder="No links" options={screens} value={screen} onChange={setScreen} data-testid="bulk-screen" />
        </Form.Item>
        {type === 'CONNECTION_GROUP' && (
          <Form.Item label="Most lines per person" extra="Rows over this are refused. The earliest rows stay valid.">
            <InputNumber min={1} max={1000} value={maxPerGroup} onChange={(v) => setMaxPerGroup(v)} />
          </Form.Item>
        )}
        {repeat && (
          <Form.Item
            label="Why are you running these subjects again?"
            required
            validateStatus={reasonProblem && reason ? 'error' : undefined}
            help={reasonProblem ?? undefined}
          >
            <Input.TextArea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-reason" />
          </Form.Item>
        )}
      </Form>
      {columns.length > 0 && (
        <>
          <Text strong>Columns</Text>
          <Table
            size="small"
            style={{ marginTop: 8 }}
            pagination={false}
            rowKey="name"
            dataSource={columns}
            columns={[
              {
                title: 'Column',
                dataIndex: 'name',
                render: (v: string, c) => (
                  <Space size={4}>
                    <Text className="ol-mono">{v}</Text>
                    {columnRequired(c, inviting) && <Tag color="red">required</Tag>}
                  </Space>
                ),
              },
              { title: 'What goes in it', dataIndex: 'hint' },
            ]}
          />
          <Paragraph type="secondary" style={{ marginTop: 8 }}>
            Your own CRA sheets upload as they are. Extra columns must start with <Text className="ol-mono">x_</Text>.
          </Paragraph>
        </>
      )}
    </Drawer>
  );
}
